import * as React from 'react';
import { type Editor } from '@tiptap/react';

import AttachFileIcon from '@mui/icons-material/AttachFile';

import { useTiptapEditor } from 'containers/Tiptap/hooks/use-tiptap-editor';

import type { ButtonProps } from 'containers/Tiptap/components/tiptap-ui-primitive/button';

import { Button } from 'containers/Tiptap/components/tiptap-ui-primitive/button';

export interface ImageUploadButtonProps extends ButtonProps {
  editor?: Editor | null;
  text?: string;
  extensionName?: string;
}

export function isImageActive(editor: Editor | null, extensionName: string): boolean {
  if (!editor) return false;
  return editor.isActive(extensionName);
}

export function insertImage(editor: Editor | null, extensionName: string): boolean {
  if (!editor) return false;

  return editor
    .chain()
    .focus()
    .insertContent({
      type: extensionName,
    })
    .run();
}

export function useImageUploadButton(
  editor: Editor | null,
  extensionName: string = 'imageUpload',
  disabled: boolean = false
): {
  isActive: boolean;
  handleInsertImage: () => boolean;
} {
  const isActive = isImageActive(editor, extensionName);
  const handleInsertImage = React.useCallback(() => {
    if (disabled) return false;
    return insertImage(editor, extensionName);
  }, [editor, extensionName, disabled]);

  return {
    isActive,
    handleInsertImage,
  };
}

export const ImageUploadButton = React.forwardRef<HTMLButtonElement, ImageUploadButtonProps>(
  (
    {
      editor: providedEditor,
      extensionName = 'imageUpload',
      text,
      className = '',
      disabled,
      onClick,
      children,
      ...buttonProps
    },
    ref
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const { isActive, handleInsertImage } = useImageUploadButton(editor, extensionName, disabled);

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);

        if (e.defaultPrevented || disabled) return;

        handleInsertImage();
      },
      [onClick, disabled, handleInsertImage]
    );

    if (!editor || !editor.isEditable) {
      return null;
    }

    return (
      <Button
          ref={ref}
          type="button"
          className={className.trim()}
          data-style="ghost"
          data-active-state={isActive ? 'on' : 'off'}
          role="button"
          tabIndex={-1}
          aria-label="Add files"
          aria-pressed={isActive}
          tooltip="Add files"
          onClick={handleClick}
          disabled={disabled}
          {...buttonProps}
        >
          {children || (
            <>
              <AttachFileIcon />
              {text && <span className="tiptap-button-text">{text}</span>}
            </>
          )}
        </Button>
    );
  }
);

ImageUploadButton.displayName = 'ImageUploadButton';

export default ImageUploadButton;
